export interface RaceGridEntitySlot {
  driverId?: number | null;
  carId?: number | null;
}

function validEntityId(id: number | null | undefined): id is number {
  return typeof id === 'number' && Number.isFinite(id) && id > 0;
}

export function isDriverIdTaken(
  slots: readonly RaceGridEntitySlot[],
  driverId: number,
  excludeIndex?: number
): boolean {
  if (!validEntityId(driverId)) return false;
  return slots.some(
    (slot, index) =>
      index !== excludeIndex && validEntityId(slot.driverId) && slot.driverId === driverId
  );
}

export function isCarIdTaken(
  slots: readonly RaceGridEntitySlot[],
  carId: number,
  excludeIndex?: number
): boolean {
  if (!validEntityId(carId)) return false;
  return slots.some(
    (slot, index) => index !== excludeIndex && validEntityId(slot.carId) && slot.carId === carId
  );
}

export function gridHasUniqueDriversAndCars(slots: readonly RaceGridEntitySlot[]): boolean {
  const drivers = new Set<number>();
  const cars = new Set<number>();
  for (const slot of slots) {
    if (validEntityId(slot.driverId)) {
      if (drivers.has(slot.driverId)) return false;
      drivers.add(slot.driverId);
    }
    if (validEntityId(slot.carId)) {
      if (cars.has(slot.carId)) return false;
      cars.add(slot.carId);
    }
  }
  return true;
}

function shuffleWithRandom<T>(items: T[], random: () => number): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function assignUniqueDriverCarSlots<
  T extends { id?: number | null },
  C extends { id?: number | null },
>(options: {
  count: number;
  reservedDriverIds?: readonly number[];
  reservedCarIds?: readonly number[];
  drivers: readonly T[];
  cars: readonly C[];
  random?: () => number;
}): { driver: T | null; car: C | null; driverId: number | null; carId: number | null }[] {
  const random = options.random ?? Math.random;
  const reservedDrivers = new Set(
    (options.reservedDriverIds ?? []).filter((id) => validEntityId(id))
  );
  const reservedCars = new Set((options.reservedCarIds ?? []).filter((id) => validEntityId(id)));

  const driverPool = shuffleWithRandom(
    options.drivers.filter((d) => validEntityId(d.id) && !reservedDrivers.has(d.id)),
    random
  );
  const carPool = shuffleWithRandom(
    options.cars.filter((c) => validEntityId(c.id) && !reservedCars.has(c.id)),
    random
  );

  const result: {
    driver: T | null;
    car: C | null;
    driverId: number | null;
    carId: number | null;
  }[] = [];

  for (let i = 0; i < options.count; i++) {
    const driver = driverPool[i] ?? null;
    const car = carPool[i] ?? null;
    result.push({
      driver,
      car,
      driverId: validEntityId(driver?.id) ? driver.id : null,
      carId: validEntityId(car?.id) ? car.id : null,
    });
  }

  return result;
}
